import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	Typography,
} from '@mui/material';
import { ReactNode, useId } from 'react';
import { useFormContext, useController, useWatch } from 'react-hook-form';
import { z } from 'zod';

export interface IMultiSelectFieldsProps {
	name: string;
	groupLabel: string | ReactNode;
	required?: boolean;
	formHelperText?: string;
	multiSelectOptions: {
		label: string;
		value: string | number;
		extendedOptions?: ReactNode;
	}[];
	allowNone?: boolean;
}

type ValueOf<T> = T[keyof T];

export const multiSelectOptionsSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			// @ts-ignore
			return z.object({ [fieldName]: validation });
		},
	};
})({
	default: z.string().array().min(1, 'Required'),
	max: (maxSelected: number) => z.string().array().max(maxSelected, `Max ${maxSelected} selections allowed`),
});

export default function MultiSelectFields({
	name,
	groupLabel,
	required = true,
	formHelperText,
	multiSelectOptions,
	allowNone = true,
}: IMultiSelectFieldsProps) {
	const { control } = useFormContext();
	const labelId = useId();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const fieldValues = useWatch({ control, name }) as (string | number)[];

	const handleChange = (value: string | number) => {
		let newFieldValues = [...fieldValues];

		if (newFieldValues.length < 1) {
			newFieldValues.push(value);
		} else if (!newFieldValues.includes(value)) {
			if (isNone(value)) {
				newFieldValues = [value];
			} else {
				newFieldValues.push(value);
				newFieldValues = newFieldValues.filter((element) => !isNone(element));
			}
		} else {
			newFieldValues.splice(newFieldValues.indexOf(value), 1);
		}
		field.onChange(newFieldValues);
	};

	const error = errors[name];

	const isNone = (val: string | number): boolean => val === 'none';

	const getColumns = (size: 'sm' | 'md', optionsCount: number) => {
		if (size === 'sm') {
			return optionsCount >= 10 ? 6 : 12;
		} else if (size === 'md') {
			if (optionsCount > 10) {
				return 4;
			} else if (optionsCount <= 10 && optionsCount > 5) {
				return 6;
			} else {
				return 12;
			}
		}
	};

	if (allowNone && !isNone(multiSelectOptions[0].value)) {
		multiSelectOptions.unshift({
			label: 'None',
			value: 'none',
		});
	}

	return (
		<FormControl fullWidth error={Boolean(error)} required={required} component="fieldset">
			<FormLabel id={labelId} component="legend">
				<Typography variant="bodyMediumBook">{groupLabel}</Typography>
			</FormLabel>
			<FormGroup>
				<Grid container>
					<Grid container columns={12}>
						{multiSelectOptions.map((option) => (
							<Grid
								item
								key={option.value}
								xs={12}
								sm={isNone(option.value) ? 12 : getColumns('sm', multiSelectOptions.length)}
								md={isNone(option.value) ? 12 : getColumns('md', multiSelectOptions.length)}
							>
								<FormControlLabel
									control={
										<Checkbox
											onChange={() => {
												handleChange(option.value);
											}}
											checked={
												Array.isArray(field.value) &&
												field.value.some((checked) => checked === option.value)
											}
											name={option.label}
											color="secondary"
										/>
									}
									label={option.label}
									componentsProps={{
										typography: {
											variant: 'bodyMediumBook',
											color: 'text.secondary',
										},
									}}
								/>
								{option.extendedOptions ? option.extendedOptions : null}
							</Grid>
						))}
					</Grid>
				</Grid>
			</FormGroup>

			<FormHelperText>
				<>{typeof error !== 'boolean' && error?.message ? error.message : formHelperText}</>
			</FormHelperText>
		</FormControl>
	);
}
